import { getPublicApiUrl, getServerApiUrl } from './runtime-config';

import { setTokenCookie } from '@/app/actions';

class MyError extends Error {
    constructor(message, status, details) {
        super(message);
        this.name = 'MyError';
        this.message = message;
        this.status = status;
        this.details = details;
    }
}

// Verrou global pour éviter d'émettre plusieurs fois l'événement UNAUTHORIZED
let unauthorizedEventEmitted = false;
let unauthorizedTimeout = null;

class MyFetchInstance {
    buildRequestDetails(url, cleanEndpoint, fetchOptions, extra = {}) {
        return {
            endpoint: cleanEndpoint,
            method: fetchOptions?.method,
            url,
            ...extra,
        };
    }

    async parseErrorResponse(response) {
        const contentType = response.headers.get('content-type')?.toLowerCase() || '';
        const baseDetails = {
            status: response.status,
            statusText: response.statusText,
            contentType,
        };

        try {
            if (contentType.includes('application/json') || contentType.includes('+json')) {
                const parsed = await response.json();
                return {
                    message: parsed?.message || response.statusText || 'HTTP_ERROR',
                    details: {
                        ...baseDetails,
                        ...(parsed?.details && typeof parsed.details === 'object' ? parsed.details : { responseDetails: parsed?.details }),
                    },
                };
            }

            const rawBody = await response.text();
            return {
                message: response.statusText || 'HTTP_ERROR',
                details: {
                    ...baseDetails,
                    rawBody: rawBody?.slice(0, 1000),
                },
            };
        } catch (parseError) {
            return {
                message: response.statusText || 'HTTP_ERROR',
                details: {
                    ...baseDetails,
                    parseError: parseError instanceof Error ? parseError.message : String(parseError),
                },
            };
        }
    }

    getBaseUrl() {
        const baseURL = typeof window === 'undefined' ? getServerApiUrl() : getPublicApiUrl();
        if (!baseURL) {
            return '';
        }

        return baseURL;
    }

    async request(endpoint = '', options = {}, returnHeaders = false) {
        const baseUrl = this.getBaseUrl();
        if (!baseUrl) {
            throw new Error('API_URL is not configured. Please set NEXT_PUBLIC_API_URL environment variable.');
        }
        
        const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
        const url = `${baseUrl}/${cleanEndpoint}`;
        // Set up headers
        const headers = {
            'Content-Type': 'application/json',
            'X-Front': 'web',
            ...options.headers,
        };

        // On évite de définir le Content-Type pour les FormData
        if (options.body instanceof FormData) {
            delete headers['Content-Type'];
        } else {
            options.body = JSON.stringify(options.body);
        }

        // Fusion des en-têtes personnalisés avec les options
        const fetchOptions = {
            ...options,
            cache: 'no-store',
            credentials: 'include',
            headers,
        };

        try {
            const response = await fetch(url, fetchOptions);

            // Nouveau token
            const newToken = response.headers.get('X-New-Token');
            if (newToken) {
                // Détection du contexte d'exécution
                const isEdgeRuntime = typeof EdgeRuntime !== 'undefined';
                const isClient = typeof window !== 'undefined';

                if (isEdgeRuntime) {
                    // Middleware : forcer le retour des headers pour que le middleware gère le cookie
                    returnHeaders = true;
                } else if (!isClient) {
                    // Server Action/Component : appeler setTokenCookie directement
                    await setTokenCookie(newToken);
                } else {
                    // Côté client : appeler l'API Route pour mettre à jour le cookie httpOnly
                    // IMPORTANT: Attendre que le token soit mis à jour avant de continuer
                    await fetch('/api/auth/update-token', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ token: newToken }),
                        credentials: 'include',
                    });
                }
            }

            if (response.ok) {
                // If the response is empty, return null
                if (response.status === 204) {
                    return null;
                }

                // Get all headers
                const headers = {};
                response.headers.forEach((value, key) => {
                    headers[key] = value;
                });

                if (!headers['content-type']) {
                    return null;
                }

                // Handle different response types
                const contentType = headers['content-type'];
                const normalizedContentType = contentType.toLowerCase();
                let parsedResponse;
                switch (true) {
                    case normalizedContentType.includes('application/json'):
                    case normalizedContentType.includes('+json'):
                        parsedResponse = await response.json();
                        break;
                    case normalizedContentType.includes('text/html'):
                        parsedResponse = await response.text();
                        break;
                    case normalizedContentType.includes('application/pdf'):
                    case normalizedContentType.includes('application/zip'):
                    case normalizedContentType.includes('application/x-zip-compressed'):
                    case normalizedContentType.includes('application/blob'):
                    case normalizedContentType.includes('application/vnd.openxmlformats-officedocument.wordprocessingml.document'):
                    case normalizedContentType.startsWith('image/'):
                        parsedResponse = await response.blob();
                        break;
                    case normalizedContentType.includes('text/plain'):
                        parsedResponse = await response.text();
                        break;
                    case normalizedContentType.includes('application/octet-stream'):
                        parsedResponse = await response.arrayBuffer();
                        break;
                    case normalizedContentType.includes('multipart/form-data'):
                        parsedResponse = await response.formData();
                        break;
                    default:
                        throw new Error(`Fetch error on "${cleanEndpoint}" | Unsupported response type: ${contentType}`);
                }
                return returnHeaders ? { headers, data: parsedResponse } : parsedResponse;
            }

            // Handle HTTP errors
            const parsedError = await this.parseErrorResponse(response);

            throw new MyError(
                parsedError.message,
                response.status,
                this.buildRequestDetails(url, cleanEndpoint, fetchOptions, parsedError.details),
            );
        } catch (err) {
            if (typeof window !== 'undefined' && err.status === 401) {
                // On émet un événement personnalisé pour la gestion de l'authentification
                // Mais seulement si aucun événement n'a été émis récemment
                if (!unauthorizedEventEmitted) {
                    unauthorizedEventEmitted = true;
                    window.dispatchEvent(new CustomEvent('UNAUTHORIZED'));

                    // Réinitialiser le verrou après 3 secondes
                    if (unauthorizedTimeout) {
                        clearTimeout(unauthorizedTimeout);
                    }
                    unauthorizedTimeout = setTimeout(() => {
                        unauthorizedEventEmitted = false;
                        unauthorizedTimeout = null;
                    }, 3000);
                }
                throw err;
            }
            if (err instanceof MyError) {
                throw err;
            }

            throw new MyError(
                err?.message || 'NETWORK_ERROR',
                err?.status,
                this.buildRequestDetails(url, cleanEndpoint, fetchOptions, {
                    errorName: err?.name,
                    originalMessage: err?.message,
                    hint: 'Network-level failure: the browser did not expose an HTTP response. Check proxy/load-balancer logs for this URL and method.',
                }),
            );
        }
    }

    get(endpoint, options = {}, returnHeaders = false) {
        return this.request(endpoint, { ...options, method: 'GET' }, returnHeaders);
    }

    post(endpoint, body, options = {}, returnHeaders = false) {
        return this.request(endpoint, {
            ...options,
            method: 'POST',
            body,
        }, returnHeaders);
    }

    patch(endpoint, body, options = {}, returnHeaders = false) {
        return this.request(endpoint, {
            ...options,
            method: 'PATCH',
            body,
        }, returnHeaders);
    }

    put(endpoint, body, options = {}, returnHeaders = false) {
        return this.request(endpoint, {
            ...options,
            method: 'PUT',
            body,
        }, returnHeaders);
    }

    delete(endpoint, options = {}, returnHeaders = false) {
        return this.request(endpoint, { ...options, method: 'DELETE' }, returnHeaders);
    }

    /**
     * Adapter for dynamic method access with case-insensitive HTTP verbs
     * Allows using: myFetch.GET(), myFetch.get(), myFetch.Post(), etc.
     */
    static createAdapter(instance) {
        return new Proxy(instance, {
            get(target, prop) {
                // If the property is a standard method or not a string, return it directly
                if (typeof prop !== 'string' || prop in target) {
                    return target[prop];
                }

                // Normalize HTTP method name to lowercase
                const methodLower = prop.toLowerCase();
                const validMethods = ['get', 'post', 'patch', 'put', 'delete'];

                if (validMethods.includes(methodLower)) {
                    return target[methodLower].bind(target);
                }

                // Return undefined for unknown properties
                return undefined;
            },
        });
    }
}

const myFetch = MyFetchInstance.createAdapter(new MyFetchInstance());

export default myFetch;